import React, { useState, useEffect } from 'react';
import { personalProjectsAPI } from '../services/api';

const PersonalProjects = ({ 
  userProjects, 
  setUserProjects, 
  projectsLoading, 
  setProjectsLoading,
  getCachedProjects,
  onEditProject,
  onRemoveProject,
  onShowAllProjects,
  onAddProject
}) => {

  // Load cached data first for instant display
  useEffect(() => {
    const cachedProjects = getCachedProjects();
    if (cachedProjects.length > 0) {
      setUserProjects(cachedProjects);
    }
    // Then fetch fresh data in background
    fetchUserProjects();
  }, [getCachedProjects]);

  const fetchUserProjects = async () => {
    setProjectsLoading(true);
    try {
      const response = await personalProjectsAPI.getAll();
      if (response.success) {
        setUserProjects(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching user projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };



  // Format date range with styled Present badge
  const formatDateRangeWithBadge = (startDate, endDate, isCurrent) => {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
    };

    const start = formatDate(startDate);
    
    if (isCurrent) {
      return (
        <>
          {start} - <span className="present-badge">Present</span>
        </>
      );
    } else {
      const end = formatDate(endDate);
      return `${start} - ${end}`;
    }
  };

  // Format project type for display
  const formatProjectType = (projectType) => {
    const typeMap = {
      'personal': 'Personal Project',
      'academic': 'Academic Project',
      'final_year': 'Final Year Project',
      'hackathon': 'Hackathon Project',
      'open_source': 'Open Source Contribution'
    };
    return typeMap[projectType] || projectType || 'Project';
  };

  // Format status for display
  const formatStatus = (status) => {
    const statusMap = {
      'completed': 'Completed',
      'ongoing': 'Ongoing',
      'abandoned': 'Abandoned',
      'draft': 'Draft',
      'published': 'Published'
    };
    return statusMap[status] || status || 'Completed';
  };

  return (
    <div className="personal-projects-section">
      <div className="section-header">
        <h3>Projects</h3>
        <button 
          className="header-btn add-skills-btn"
          onClick={onAddProject}
          title="Add Project"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span>Add Project</span>
        </button>
      </div>

      <div className="experience-container">
        {projectsLoading ? (
          <div className="experience-loading">
            <div className="spinner-small"></div>
            <span>Loading projects...</span>
          </div>
        ) : userProjects && userProjects.length > 0 ? (
          <div className="experience-list">
            {userProjects.slice(0, 3).map((project, index) => (
              <div key={project.id} className={`professional-project-card ${project.is_ongoing ? 'current-project' : ''}`}>
                {/* Professional Header */}
                <div className="project-header">
                  <div className="project-title-section">
                    <h3 className="project-title">{project.title}</h3>
                    <div className="project-meta">
                      <span className="project-type">{formatProjectType(project.project_type)}</span>
                      <span className="project-separator">•</span>
                      <span className={`project-status ${project.status || 'completed'}`}>
                        <span className="status-indicator"></span>
                        {formatStatus(project.status)}
                      </span>
                    </div>
                    {project.experience && (
                      <div className="project-experience">
                        <span className="experience-label">Associated with:</span>
                        <span className="experience-value">
                          {project.experience.title} at {project.experience.company}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="project-actions">
                    <button 
                      className="edit-project-btn"
                      onClick={() => onEditProject(project)}
                      title="Edit project"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                      </svg>
                    </button>
                    <button 
                      className="remove-project-btn"
                      onClick={() => onRemoveProject(project.id)}
                      title="Remove project"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3,6 5,6 21,6"></polyline>
                        <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Project Content */}
                <div className="project-content">
                  {/* Project Duration */}
                  <div className="project-duration">
                    <span className="duration-label">Duration:</span>
                    <span className="duration-value">
                      {project.date_range || formatDateRangeWithBadge(project.start_date, project.end_date, project.is_ongoing)}
                    </span>
                  </div>
                  
                  {/* Project Description */}
                  {project.description && (
                    <div className="project-description">
                      <p>{project.description}</p>
                    </div>
                  )}
                  
                  {/* Technologies */}
                  {project.technologies_used && project.technologies_used.length > 0 && (
                    <div className="project-technologies">
                      <span className="technologies-label">Technologies:</span>
                      <span className="technologies-list">
                        {project.technologies_used.join(', ')}
                      </span>
                    </div>
                  )}

                </div>
              </div>
            ))}
            
            {userProjects.length > 3 && (
              <div className="view-all-experiences">
                <button 
                  className="view-all-btn"
                  onClick={async () => {
                    await fetchUserProjects();
                    onShowAllProjects();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 3h18v18H3zM9 9h6v6H9z"></path>
                    <path d="M9 3v6M15 3v6M9 15v6M15 15v6"></path>
                  </svg>
                  View All {userProjects.length} Projects
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="no-experience">
            <div className="no-experience-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                <polyline points="14,2 14,8 20,8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10,9 9,9 8,9"></polyline>
              </svg>
            </div>
            <div className="no-experience-content">
              <h4>Showcase Your Rising Talent</h4>
              <p>Build your portfolio with personal projects, academic work, and creative endeavors to demonstrate your potential</p>
              <div className="no-experience-features">
                <div className="feature-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                  <span>Personal & Academic Projects</span>
                </div>
                <div className="feature-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                  <span>Hackathon & Open Source Work</span>
                </div>
                <div className="feature-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                  <span>Demonstrate Your Skills</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default PersonalProjects;
